<template>
  <div class="px-6 py-6 space-y-6 container">
    <div class="space-y-6 w-150">
      <div v-if="llmProvider === 'openai'">
        <TextInput
          v-model="apiKey"
          label="OpenAI key"
          placeholder=""
          :disabled="!isAdmin"
          @keydown.enter="configureStudiesQA(apiKey)"
        >
          <template #label="{ id }">
            <div class="flex items-center gap-2">
              <label class="text-sm text-primary font-medium pointer-events-none" :for="id">
                Enter you OpenAI key
              </label>
            </div>
          </template>
          <template #suffix>
            <button
              v-if="isAdmin"
              class="text-white px-2 py-1 bg-primary-500 rounded-md text-sm hover:brightness-110"
              @click="configureStudiesQA(apiKey)"
            >
              Save
            </button>
          </template>
        </TextInput>
      </div>
      <div v-if="llmProvider === 'lmstudio'">
        <p class="text-sm text-gray-600">
          LM Studio server URL is configured via the
          <code class="text-xs bg-gray-100 px-1 rounded">LLM_PROVIDER_URL</code> environment
          variable. Start LM Studio, load a model, and enable the local server.
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import TextInput from "@app/components/Global/Inputs/TextInput.vue";
import useLoading from "@app/composables/use-loading";
import useSnackbar from "@app/composables/use-snackbar";
import useAiAssistant from "@app/composables/useAiAssistant";
import { AuthKey } from "@app/injectionKeys";
import { injectStrict } from "@app/utils/injectStrict";
import { Role } from "@core/domain/types/role.type";
import { computed, onMounted } from "vue";

const { apiKey, llmProvider, configure, initLlmProvider, validateOpenAiKey } = useAiAssistant();
const loading = useLoading();
const snackbar = useSnackbar();
const { user } = injectStrict(AuthKey);
const isAdmin = computed(() => user.value?.role === Role.ADMIN);

onMounted(async () => {
  await initLlmProvider();
});

async function configureStudiesQA(apiKey: string) {
  loading.start();
  try {
    if (apiKey.trim() !== "") {
      await validateOpenAiKey(apiKey);
    }
    await configure(apiKey);
    snackbar.success("Success");
  } catch (err: any) {
    const message =
      err?.response?.status === 401
        ? "Invalid OpenAI API key"
        : "Error while configuring StudiesQA key. Please check your key and try again.";
    snackbar.error(message);
  } finally {
    loading.stop();
  }
}
</script>
